/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/emailFlow.scss';
import {AwesomeIcon, ChangeFunction, ElementPosition, Link, Loader} from '@dvrd/dvr-controls';
import checkAll from '@iconify/icons-bi/check-all';
import pauseCircle from '@iconify/icons-feather/pause-circle';
import {Icon} from '@iconify/react';
import classNames from 'classnames';
import React, {KeyboardEventHandler, MouseEventHandler, useContext, useMemo, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import EmailStep from '../../models/emailStepModel';
import EmailTemplate from '../../models/emailTemplateModel';
import {translateEmailStepError} from '../../util/bolValidation';
import {EMAIL_SETTINGS_ROUTE, NO_REPLY_FLEX_KEY} from '../../util/constants';
import {AccessKey, EmailStatLoader, EmailStepStatTotal} from '../../util/interfaces';
import {getMenuWidth} from '../../util/menuUtil';
import {EmailStepError} from '../../util/types';
import {numberTrim} from '../../util/utils';
import {BolmateButton, BolmateInput} from '../controls';
import BolmateSwitch from '../controls/switch/bolmateSwitch';
import {AppContext} from '../misc/appContext';
import InfoBanner from '../misc/infoBanner';
import useRight from '../misc/useRight';
import Step from './emailStep/step';

interface Props {
    onReplaceStep: (step: EmailStep) => void;
    onDeleteStep: (step: EmailStep) => void;
    onOpenStats: (step?: EmailStep) => MouseEventHandler;
    onClickTemplate: (step: EmailStep) => MouseEventHandler;
    onChangeActive: ChangeFunction<boolean>;
    onAddStep: MouseEventHandler;
    onGlobalLoad: (loading: boolean) => void;
    onSendHistoryMails: MouseEventHandler;
    onCloseSendHistoryMails: MouseEventHandler;
    onClickImport: MouseEventHandler;
    allowChange: (step: EmailStep) => boolean;
    onNavigateTemplate?: (template: EmailTemplate, step: EmailStep, returnPage?: string) => void;
    onChangeSearch: ChangeFunction<string>;
    onSearch: MouseEventHandler & KeyboardEventHandler;
    onLoadStepStats: (loader: EmailStatLoader) => void;
    onCancelEmails: MouseEventHandler;
    onActivationChanged: VoidFunction;
    loading: boolean;
    steps: Array<EmailStep>;
    templates: Array<EmailTemplate>;
    flowIsActive: boolean;
    warnChange: boolean;
    search: string;
    globalStats: EmailStepStatTotal;
    hasPlannedEmails: boolean;
    errors: Array<EmailStepError>;
}

export default function EmailStepFlow(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const [canEditAuto, canEdit, canCopy, canAccessEmailSettings] = useRight(
        AccessKey.EMAIL_EDIT_AUTOMATIC,
        AccessKey.EMAIL_EDIT,
        AccessKey.EMAIL_COPY,
        AccessKey.PAGE_SETTINGS_EMAILS
    );
    const {user} = context.userContext;
    const {account, accounts} = context.accountContext;
    const {
        loading,
        onOpenStats,
        steps,
        onReplaceStep,
        templates,
        onDeleteStep,
        errors,
        flowIsActive,
        onChangeActive,
        allowChange,
        warnChange,
        onAddStep,
        onGlobalLoad,
        onClickTemplate,
        globalStats,
        onSendHistoryMails,
        onCloseSendHistoryMails,
        onClickImport,
        onNavigateTemplate,
        search,
        onSearch,
        onChangeSearch,
        onLoadStepStats,
        onCancelEmails,
        hasPlannedEmails,
        onActivationChanged
    } = props;
    const [searchVisible, setSearchVisible] = useState(false);
    const [autoIcon, autoText] = useMemo(() => {
        if (flowIsActive)
            return [
                <Icon icon={checkAll} className='enabled-icon' />,
                t('email.flow.autoMailOn', 'Automatisch mailen aan')
            ];
        return [
            <Icon icon={pauseCircle} className='disabled-icon' />,
            t('email.flow.autoMailOff', 'Automatisch mailen uit')
        ];
    }, [flowIsActive, t]);

    function onToggleSearch() {
        setSearchVisible(true);
    }

    function renderToggles() {
        return (
            <div className='toggles-container'>
                <div className={classNames('toggle-container auto-mail', warnChange && 'warn')}>
                    <div className='row'>
                        <label className='toggle-text'>
                            {autoIcon} {autoText}
                        </label>
                    </div>
                    <div className='row'>
                        <label>{t('email.flow.status', 'Status')}</label>
                        <BolmateSwitch
                            value={flowIsActive}
                            onChange={onChangeActive}
                            labels={[flowIsActive ? t('email.flow.on', 'AAN') : t('email.flow.off', 'UIT')]}
                            className='switch'
                            labelClassName='switch-label'
                            disabled={!canEditAuto}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderHistoryButton() {
        if (!canEdit || !account?.settings || !account.historyImported || account.settings.historySent) return <div />;
        if (account.getFlex('history_mails_denied') === true) return <div />;
        return (
            <div>
                <BolmateButton
                    onClick={onSendHistoryMails}
                    label={t('email.flow.bulkMailing', 'Bulk mailing naar 30 dagen import...')}
                    className='history-mail-btn'
                    ornaments={[
                        {element: <AwesomeIcon name='envelope' />},
                        {
                            element: (
                                <AwesomeIcon name='times' className='close-btn' onClick={onCloseSendHistoryMails} />
                            ),
                            placement: ElementPosition.RIGHT
                        }
                    ]}
                />
            </div>
        );
    }

    function renderImportButton() {
        if (!canCopy || accounts.length <= 1) return null;
        return (
            <Link onClick={onClickImport} disabled={loading} className='btn-copy'>
                <AwesomeIcon name='copy' />
                &nbsp;&nbsp;{t('email.flow.copyFromAccount', 'Kopiëren uit ander account...')}
            </Link>
        );
    }

    function renderStep(step: EmailStep, index: number) {
        const stepErrors = errors.filter((error: EmailStepError) => {
            if (error.reason === 'content') return error.step_id === step.id;
            else if (error.reason === 'overlap') return error.step1_id === step.id;
            return false;
        });
        return (
            <Step
                key={index}
                onReplaceStep={onReplaceStep}
                onDeleteStep={onDeleteStep}
                onOpenStats={onOpenStats}
                onClickTemplate={onClickTemplate(step)}
                allowChange={allowChange}
                step={step}
                templates={templates}
                index={index + 1}
                showWarning={!!stepErrors.length}
                errors={stepErrors}
                onGlobalLoad={onGlobalLoad}
                onNavigateTemplate={onNavigateTemplate}
                onLoadStepStats={onLoadStepStats}
                onActivationChanged={onActivationChanged}
            />
        );
    }

    function renderTrackingDisabledInfo() {
        const enabled = account?.getFlex(NO_REPLY_FLEX_KEY) === true;
        if (enabled) return null;
        const message = t(
            'email.flow.noTrackingMessage',
            'Er worden geen open- en click-aantallen bijgehouden vanwege geen gebruik no-reply e-mailadres.'
        );
        return (
            <InfoBanner
                persistDismiss={false}
                actionLabel={canAccessEmailSettings ? t('email.flow.moreInfo', 'Meer informatie') : undefined}
                actionRoute={canAccessEmailSettings ? EMAIL_SETTINGS_ROUTE : undefined}
            >
                {message}
            </InfoBanner>
        );
    }

    function renderMessages() {
        const trackingEnabled = account?.getFlex(NO_REPLY_FLEX_KEY) === true;
        const firstOverlapError = errors.find((error: EmailStepError) => error.reason === 'overlap');
        if (!firstOverlapError && trackingEnabled) return null;
        return (
            <div className='texts-container'>
                {!!errors.length && (
                    <div className='warn-policy-container'>
                        <AwesomeIcon name='exclamation-circle' className='warn-icon' />
                        <label className='warn-policy'>
                            {t('email.flow.policyWarnPrefix', 'Je e-mails voldoen niet aan de')}{' '}
                            <Link route='https://partnerplatform.bol.com/nl/hulp-nodig/prestaties/beleidspunten-zelfpromotie/'>
                                {t('email.flow.policyLink', 'beleidspunten')}
                            </Link>{' '}
                            {t('email.flow.policyWarnSuffix', 'van Bol.')}{' '}
                            {translateEmailStepError(firstOverlapError, true)}
                        </label>
                    </div>
                )}
                {renderTrackingDisabledInfo()}
            </div>
        );
    }

    function renderSearch() {
        return (
            <div className='search-container'>
                {searchVisible ? (
                    <>
                        <BolmateInput
                            onChange={onChangeSearch}
                            value={search}
                            onEnter={onSearch}
                            label={t('email.flow.search', 'Zoeken')}
                            inputProps={{placeholder: t('email.flow.searchPlaceholder', 'Zoek een e-mail')}}
                            ornaments={{element: <AwesomeIcon name='search' />}}
                            className='search-field'
                        />
                        <BolmateButton onClick={onSearch} label={t('email.flow.search', 'Zoeken')} secondary />
                    </>
                ) : (
                    <>
                        <Link onClick={onToggleSearch} disabled={loading} className='btn-copy'>
                            <AwesomeIcon name='search' />
                            &nbsp;&nbsp;{t('email.flow.searchEmails', 'E-mails zoeken')}
                        </Link>
                    </>
                )}
                {renderImportButton()}
                {!flowIsActive && hasPlannedEmails && (
                    <Link onClick={onCancelEmails} className='cancel-link' disabled={loading}>
                        <AwesomeIcon name='times' />
                        &nbsp;&nbsp;{t('email.flow.cancelPlanned', 'Ingeplande e-mails annuleren…')}
                    </Link>
                )}
            </div>
        );
    }

    function renderGlobalStats() {
        if (steps.length < 2) return null;
        const {SENT, OPEN, CLICK} = globalStats;
        const loading = SENT === -1;
        return (
            <div className='email-stats-button global' onClick={onOpenStats()}>
                <Loader active={loading} size='3rem' />
                <div className='stat-container'>
                    <label>{t('email.flow.sent', 'Verstuurd')}</label>
                    <label>{loading ? '-' : numberTrim(SENT)}</label>
                </div>
                <AwesomeIcon name='arrow-up-right-from-square' className='icon-container' />
                <div className='stat-container'>
                    <label>{t('email.flow.open', 'Open')}</label>
                    <label>{loading ? '-' : SENT === 0 ? 0 : ((OPEN / SENT) * 100).toFixed(1)}%</label>
                </div>
                <div className='stat-container'>
                    <label>{t('email.flow.click', 'Klik')}</label>
                    <label>{loading ? '-' : numberTrim(CLICK)}</label>
                </div>
            </div>
        );
    }

    function renderAddButton() {
        if (!canEdit) return null;
        return (
            <div className='add-step' onClick={onAddStep}>
                <div className='line top rel' />
                <div className='plus-circle'>
                    <AwesomeIcon name='plus' className='plus-icon' />
                </div>
                <label className='plus-label'>
                    {t('email.flow.addEmail', 'Voeg een mail toe aan de E-mail Volgorde')}
                </label>
            </div>
        );
    }

    function renderReviewsExpectation() {
        return (
            <InfoBanner featureKey='reviews-expectation' persistDismiss variant='info'>
                🍒&nbsp;<i>Patience is bitter, but its fruit is sweet.</i> - Aristoteles
                <br />
                <br />
                {t(
                    'email.flow.expectation-1',
                    'Jouw reviews hebben tijd nodig om te groeien. In e-commerce laat gemiddeld 1 op de 40 klanten een review achter.\nMeer reviews geven meer bestellingen - geef dit vliegwiel de tijd om op gang te komen.'
                )}
                {user?.subscription?.isTrialing &&
                    ' ' + t('email.flow.expectation-trial', 'Verlenging van jouw 14 dagen proef? Mail ons.')}
                <br />
                <br />
                {t(
                    'email.flow.expectation-2',
                    'Met slimme e-mailcontent kun je jouw conversie ratio flink verbeteren tot wel 1 review per 7 klanten.'
                )}
                &nbsp;
                {/* prettier-ignore */}
                <Trans i18nKey='email.flow.expectationLink'>
                    Lees <a
                        href='https://bolmate.nl/de-best-presterende-e-mail-campagnes-voor-review-op-bol/'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        hier
                    </a> hoe.
                </Trans>
            </InfoBanner>
        );
    }

    const menuWidth = getMenuWidth();
    return (
        <>
            <InfoBanner
                featureKey='emails'
                variant='info'
                title={t('email.flow.emailsTitle', 'E-mails')}
                showForNewUsersOnly={true}
            >
                {t(
                    'email.flow.intro',
                    'Automatiseer e-mails aan jouw klanten zodat je zonder handwerk meer reviews op bol behaalt en/of facturen verstuurt.'
                )}
            </InfoBanner>
            {renderReviewsExpectation()}
            <div className='email-flow' style={{'--menu-width': `${menuWidth}px`} as React.CSSProperties}>
                <div className='flow-header-container'>
                    {renderMessages()}
                    {renderSearch()}
                    <div className='flow-header-part'>
                        {renderHistoryButton()}
                        {renderGlobalStats()}
                        {renderToggles()}
                    </div>
                </div>
                <div className='emails-container'>
                    <Loader active={loading} />
                    {steps.map(renderStep)}
                    {renderAddButton()}
                </div>
            </div>
        </>
    );
}
